import React, { useState } from "react";
import { motion } from "framer-motion";
import { useDropzone } from "react-dropzone";
import {
  FaTimes,
  FaCloudUploadAlt,
  FaFilePdf,
  FaSpinner,
  FaCheckCircle,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";

const UploadPYQModal = ({ isOpen, onClose, onSuccess, isDarkMode }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    subject: "",
    subjectCode: "",
    branch: "",
    semester: "",
    examYear: "",
    examType: "External",
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const branches = ["CSE", "ISE", "ECE", "EEE", "MECH", "CIVIL", "AIML", "DS"];
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];
  const examTypes = ["Internal", "External", "CIE", "SEE"];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) =>
    (currentYear - i).toString(),
  );

  const getBgColor = () => (isDarkMode ? "rgba(30, 30, 45, 0.95)" : "white");
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  const onDrop = (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile.size > 20 * 1024 * 1024) {
      toast.error("File size should be less than 20MB");
      return;
    }
    if (uploadedFile.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }
    setFile(uploadedFile);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    maxFiles: 1,
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a PDF file");
      return;
    }
    if (
      !formData.title ||
      !formData.subject ||
      !formData.subjectCode ||
      !formData.branch ||
      !formData.semester ||
      !formData.examYear
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    setUploading(true);
    const data = new FormData();
    Object.keys(formData).forEach((key) => data.append(key, formData[key]));
    data.append("file", file);

    try {
      const token = localStorage.getItem("token");
      await axios.post("/api/pyqs", data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("PYQ uploaded successfully!");
      setFile(null);
      setFormData({
        title: "",
        description: "",
        subject: "",
        subjectCode: "",
        branch: "",
        semester: "",
        examYear: "",
        examType: "External",
      });
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to upload PYQ");
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "1rem",
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 30 }}
        style={{
          background: getBgColor(),
          borderRadius: "24px",
          maxWidth: "600px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          padding: "1.5rem",
          border: `1px solid ${getBorderColor()}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <h2
            style={{
              color: getTextColor(),
              fontSize: "1.3rem",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <FaFilePdf color="#ef4444" /> Upload Previous Year Question Paper
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.2rem",
              cursor: "pointer",
              color: getTextColor(),
            }}
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* File Dropzone */}
          <div
            {...getRootProps()}
            style={{
              border: `2px dashed ${isDragActive ? "#8b5cf6" : getBorderColor()}`,
              borderRadius: "16px",
              padding: "1.5rem",
              textAlign: "center",
              marginBottom: "1.5rem",
              cursor: "pointer",
              background: isDragActive
                ? isDarkMode
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.05)"
                : "transparent",
              transition: "all 0.3s",
            }}
          >
            <input {...getInputProps()} />
            {file ? (
              <div>
                <FaFilePdf size={50} color="#ef4444" />
                <p
                  style={{
                    color: getTextColor(),
                    marginTop: "0.5rem",
                    fontWeight: "500",
                  }}
                >
                  {file.name}
                </p>
                <p
                  style={{
                    color: getTextColor(),
                    fontSize: "0.8rem",
                    opacity: 0.7,
                  }}
                >
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  style={{
                    marginTop: "0.5rem",
                    padding: "0.3rem 1rem",
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "20px",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div>
                <FaCloudUploadAlt size={50} color="#8b5cf6" />
                <p style={{ color: getTextColor(), marginTop: "0.5rem" }}>
                  {isDragActive
                    ? "Drop the PDF here"
                    : "Drag & drop a PDF, or click to select"}
                </p>
                <p
                  style={{
                    color: getTextColor(),
                    fontSize: "0.8rem",
                    opacity: 0.7,
                  }}
                >
                  Only PDF files (Max 20MB)
                </p>
              </div>
            )}
          </div>

          {/* Form Fields */}
          <div style={{ display: "grid", gap: "1rem" }}>
            <div>
              <label
                style={{
                  color: getTextColor(),
                  fontSize: "0.8rem",
                  fontWeight: "500",
                  marginBottom: "0.3rem",
                  display: "block",
                }}
              >
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Data Structures Question Paper"
                required
                style={{
                  width: "100%",
                  padding: "0.7rem",
                  border: `1px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>
            <div>
              <label
                style={{
                  color: getTextColor(),
                  fontSize: "0.8rem",
                  fontWeight: "500",
                  marginBottom: "0.3rem",
                  display: "block",
                }}
              >
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="2"
                placeholder="Additional details about this paper"
                style={{
                  width: "100%",
                  padding: "0.7rem",
                  border: `1px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                  resize: "vertical",
                }}
              />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label
                  style={{
                    color: getTextColor(),
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    marginBottom: "0.3rem",
                    display: "block",
                  }}
                >
                  Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="e.g., Data Structures"
                  required
                  style={{
                    width: "100%",
                    padding: "0.7rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    color: getTextColor(),
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    marginBottom: "0.3rem",
                    display: "block",
                  }}
                >
                  Subject Code *
                </label>
                <input
                  type="text"
                  name="subjectCode"
                  value={formData.subjectCode}
                  onChange={handleChange}
                  placeholder="e.g., 18CS32"
                  required
                  style={{
                    width: "100%",
                    padding: "0.7rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                    textTransform: "uppercase",
                  }}
                />
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label
                  style={{
                    color: getTextColor(),
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    marginBottom: "0.3rem",
                    display: "block",
                  }}
                >
                  Branch *
                </label>
                <select
                  name="branch"
                  value={formData.branch}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.7rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select</option>
                  {branches.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    color: getTextColor(),
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    marginBottom: "0.3rem",
                    display: "block",
                  }}
                >
                  Semester *
                </label>
                <select
                  name="semester"
                  value={formData.semester}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.7rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select</option>
                  {semesters.map((s) => (
                    <option key={s} value={s}>
                      Semester {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    color: getTextColor(),
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    marginBottom: "0.3rem",
                    display: "block",
                  }}
                >
                  Exam Year *
                </label>
                <select
                  name="examYear"
                  value={formData.examYear}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.7rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    color: getTextColor(),
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    marginBottom: "0.3rem",
                    display: "block",
                  }}
                >
                  Exam Type
                </label>
                <select
                  name="examType"
                  value={formData.examType}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "0.7rem",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  {examTypes.map((e) => (
                    <option key={e} value={e}>
                      {e}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: "0.7rem",
                background: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                fontSize: "0.9rem",
                fontWeight: "500",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              style={{
                flex: 1,
                padding: "0.7rem",
                background: uploading ? "#94a3b8" : "#8b5cf6",
                color: "white",
                border: "none",
                borderRadius: "10px",
                cursor: uploading ? "not-allowed" : "pointer",
                fontSize: "0.9rem",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              {uploading ? (
                <>
                  <FaSpinner style={{ animation: "spin 1s linear infinite" }} />{" "}
                  Uploading...
                </>
              ) : (
                <>
                  <FaCloudUploadAlt /> Upload PYQ
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default UploadPYQModal;

